
import { Component, Vue, Prop } from "vue-property-decorator";
import {
    commonModule,
    appointmentsModule,
    actionTypesModule,
    itemsModule,
    companyModule,
    appointmentsRecurrencesModule,
} from "@/store/modules/store-accessor";
import { TimeUtil } from "@/helpers/TimeUtil";
import AssigneeMultiUserCombo from "@/components/Combobox/AssigneeMultiUserCombo.vue";
import AppointmentCalendar from "@/components/Calendar/AppointmentCalendar.vue";
import EventOverlappingWarning from "@/components/Alerts/EventOverlappingWarning.vue";
import RecurrenceFields from "@/components/Appointments/RecurrenceFields.vue";
import InstallationItemWarrantyIndicator from "@/components/InstallationItemWarrantyIndicator.vue";
import _ from "lodash";
import { calcDuration, durationOptionByMinutes, getOptimalTextColor, isNumber, recurrenceFindObjectDays, primaryInstallationItemText } from "@/helpers/data";
import dayjs from "dayjs";
import { greekSearchFilter } from "@/helpers/data";

@Component({
    components: {
        AssigneeMultiUserCombo,
        AppointmentCalendar,
        EventOverlappingWarning,
        RecurrenceFields,
        InstallationItemWarrantyIndicator,
    },
})
export default class EditRecurringAppointmentSeriesModal extends Vue {
    @Prop()
    payload!: any;

    greekSearchFilter = greekSearchFilter;

    isEditingAppoinment = false;
    loadingInstallationPrimaryItems = true;
    userChangedDaysOfWeek = false;

    dateScheduledAtMenu = false;
    menu4 = false;
    recurrenceEndTypeDateMenu = false;

    payloadAppointmentSeries = {} as any;
    primaryInstallationItemsCombo = [] as any;

    appointmentSeries = {} as any;

    scheduledDateRule = [
        (val: any) => !!val || this.$i18n.t('scheduled_day_required'),
    ];

    userRule = [(val: any) => !!val || this.$i18n.t('user_required')];

    typeRule = [
        (val: any) => this.typeFound(val) || this.$i18n.t('action_required'),
    ];
    scheduledTimeRule = [
        (val: any) => !!val || this.$i18n.t('scheduled_time_required'),
    ];
    durationRule = [
        (val: any) =>
            (val || "")?.text?.length > 0 || this.$i18n.t('duration_required'),
    ];
    notesRule = [
        (val: any) => {
            if (val != null)
                return val.length <= 1000 || this.$i18n.t('notesLengthRule');
            else return true;
        },
    ];
    descriptionRule = [
        (val: any) => val?.length <= 80 || this.$i18n.t('jobDescriptionLengthRule'),
    ];

    recurrenceEndTypeDateRules = [
        (val: any) => !!val || this.$i18n.t('recurrenceEndTypeDateRequired'),
    ];

    textFound(duration: string) {
        const found = commonModule.durationCombo?.find((item: any) => {
            return item.text === duration;
        });
        return found == undefined ? false : true;
    }

    durationFound(duration: string) {
        const found: any = commonModule.durationCombo?.find((item: any) => {
            return item.text === duration;
        });
        return found == undefined ? "" : found.dur;
    }

    get durationCombo() {
        return commonModule.durationCombo;
    }

    get actionsCombo() {
        return actionTypesModule.actionTypesCombo;
    }

    get assigneeIdCombo() {
        return commonModule.personnelCombo;
    }

    optimalTextColor(color: string) {
        return getOptimalTextColor(color);
    }

    primaryItemText(item: Record<string, any>) {
        return primaryInstallationItemText(item);
    }
    
    setDurationFromActionType(actionType: any) {
        const duration = durationOptionByMinutes(this.durationCombo, actionType?.duration);
        if (duration) {
            this.appointmentSeries.duration = duration;
        }
    }

    getAssignee(name: string) {
        const combo: any = commonModule.personnelCombo;
        const foundItem = combo.find((item: any) => item.name === name);
        if (foundItem) {
            return { name: foundItem.name, id: foundItem.id };
        } else {
            return "";
        }
    }

    itemName(obj: any) {
        const installationItemDescriptionIsEnabled = (companyModule.company?.preferences as any)?.installation_item_description;
        if (installationItemDescriptionIsEnabled) {
            obj = { ...obj, primary_items: `${obj.item_description ? obj.item_description + ' | ' : ''}${obj.primary_items}` }
        }
        this.primaryInstallationItemsCombo = this.installationPrimaryItems;
        const foundItem = this.primaryInstallationItemsCombo.find(
            (item: any) =>
                item.primary_items[0] == obj.primary_items && item.id === obj.id
        );
        if (foundItem) {
            return foundItem;
        } else {
            if (obj.id) {
                let item_hidden_id = (Math.random() + 1).toString(36).substring(7);
                this.primaryInstallationItemsCombo.push({
                    ...obj,
                    disabled: true,
                    hidden_id: item_hidden_id,
                });
                return {
                    ...obj,
                    disabled: true,
                    hidden_id: item_hidden_id,
                };
            } else {
                return { primary_items: "", id: 0 };
            }
        }
    }

    async hideModal() {
        commonModule.hideModal();
    }

    get editDisabled() {
        return _.isEqual(this.appointmentSeries, this.payloadAppointmentSeries);
    }

    async beforeMount() {
        await itemsModule.getInstallationPrimaryInventoryItems(this.payload.series.installation_id);
        commonModule.getPersonnelCombo();
        commonModule.getActionCombo();
        commonModule.getDurationCombo();
        actionTypesModule.getActionTypesCombo();
        this.loadingInstallationPrimaryItems = true;
        this.loadingInstallationPrimaryItems = false;

        const foundActionType = actionTypesModule.actionTypesCombo.find(
            (item: any) => item.id === this.payload.series.action_type_id
        );
        this.payloadAppointmentSeries.type_id = {
            text: this.payload.series.type_name,
            color: foundActionType?.color || this.payload.series.type_color || "",
            cost: foundActionType?.cost ?? 0,
            id: this.payload.series.action_type_id,
        };
        this.payloadAppointmentSeries.is_recurrent = true;
        this.payloadAppointmentSeries.assignee = this.getAssignee(this.payload.series.assignee_id);
        this.payloadAppointmentSeries.assignee_id = this.payloadAppointmentSeries.assignee?.id;
        this.payloadAppointmentSeries.assignee_ids = this.payload.series.assignee_ids;
        this.payloadAppointmentSeries.assignees = this.payload.series.assignees;
        this.payloadAppointmentSeries.appointment_status_id = 2;
        this.payloadAppointmentSeries.scheduled_start = this.formatDate(this.payload.series.scheduled_start);
        this.payloadAppointmentSeries.scheduled_start_time = this.formatTime(this.payload.series.scheduled_start);
        this.payloadAppointmentSeries.duration = {
            dur: this.durationFound(
                calcDuration(
                    this.payload.series.scheduled_start,
                    this.payload.series.scheduled_end
                )
            ),
            text: this.textFound(
                calcDuration(
                    this.payload.series.scheduled_start,
                    this.payload.series.scheduled_end
                )
            )
                ? calcDuration(
                    this.payload.series.scheduled_start,
                    this.payload.series.scheduled_end
                )
                : "",
        };
        this.payloadAppointmentSeries.appointment_notes = this.payload.series.appointment_notes || '';
        this.payloadAppointmentSeries.installation_id = this.payload.series.installation_id;
        this.payloadAppointmentSeries.id = this.payload.series.id;
        this.payloadAppointmentSeries.job_id = this.payload.series.job_id || '';
        this.payloadAppointmentSeries.installation_item_id = this.itemName({
            primary_items: this.payload.series.job_item,
            id: this.payload.series.installation_item_id,
            item_type_text: this.payload.series.item_type_text,
            item_make: this.payload.series.item_make,
            item_model: this.payload.series.item_model,
            item_serial_no: this.payload.series.item_serial_no,
            item_description: this.payload.series.item_description,
        });
        this.payloadAppointmentSeries.job_description = this.payload.series.job_description || '';

        this.payloadAppointmentSeries.recurrence_type = this.payload.series.recurrence_type || null;
        this.payloadAppointmentSeries.interval = this.payload.series.interval || null;
        this.payloadAppointmentSeries.end_type = this.payload.series.end_type || null;
        this.payloadAppointmentSeries.occurrence_count = this.payload.series.occurrence_count || null;
        this.payloadAppointmentSeries.end_date = this.payload.series.end_date ? dayjs(this.payload.series.end_date).format('YYYY-MM-DD') : '';

        this.payloadAppointmentSeries.days_of_week = this.payload.series.days_of_week || null;
        this.payloadAppointmentSeries.send_client_email = this.clientHasEmail ? this.isSendClientAppointmentEmailEnabled : false;
        this.payloadAppointmentSeries.parent_appointment_id = this.payload.series.parent_appointment_id;

        this.appointmentSeries = _.cloneDeep(this.payloadAppointmentSeries);

        this.appointmentSeries.days_of_week = recurrenceFindObjectDays(this.appointmentSeries.days_of_week);
    }

    async mounted() {
        commonModule.initSnackbar({});
    }

    async updateAssignee(assignees: any) {
        this.appointmentSeries.assignee_id = assignees?.[0]?.id || null;
        this.appointmentSeries.assignee = assignees?.[0];
        this.appointmentSeries.assignee_ids = assignees;
        await commonModule.getPersonnelCombo();
        await commonModule.getActionCombo();
        await commonModule.getDurationCombo();
        await actionTypesModule.getActionTypesCombo();
        this.loadingInstallationPrimaryItems = true;
        this.loadingInstallationPrimaryItems = false;
    }

    formatDate(date: Date): string {
        return TimeUtil.formatDateOnly(date);
    }

    formatTime(date: Date): string {
        return TimeUtil.formatTimeOnly(date);
    }

    onlyNumbers(evt: any) {
        isNumber(evt);
    }

    typeFound(type: any) {
        if (type) {
            const found = this.actionsCombo?.find((item: any) => {
                return item["text"] === type.text;
            });
            return found == undefined ? false : true;
        }
        return false;
    }

    get installationPrimaryItems() {
        return itemsModule.installationPrimaryItems;
    }

    get isLoadingInstallationPrimaryItems() {
        return this.loadingInstallationPrimaryItems;
    }

    get disablePrimaryItems() {
        return (
            this.loadingInstallationPrimaryItems ||
            this.installationPrimaryItems.length === 0
        );
    }

    async doEdit() {
        if ((this.$refs.form as Vue & { validate: () => boolean }).validate()) {
            this.isEditingAppoinment = true;
            appointmentsModule.unsetCalendarAppointments();
            appointmentsModule.getCalendarAppointmentsAction(appointmentsModule.filters);
            appointmentsRecurrencesModule.editRecurrentAppointmentSeries(this.appointmentSeries);
            this.hideModal();
        }
    }

    get jobDescriptionIsEnabled() {
        return (companyModule.company?.preferences as any)?.job_description;
    }

    get hasClientSelected() {
      return this.payload.series && this.payload.series.client_email !== undefined;
    }

    get clientHasEmail() {
      if (!this.hasClientSelected) return false;
      const email = this.payload.series.client_email;
      return email !== undefined && email !== null && email !== '';
    }

    get isSendClientAppointmentEmailEnabled() {
      return (companyModule.company?.preferences as Record<string, any>)?.send_client_appointment_email;
    }

    get disableSendEmailToClient() {
      return !(companyModule.company?.preferences as Record<string, any>)?.send_client_appointment_email || !this.hasClientSelected || !this.clientHasEmail;
    }

}
